import React, { useEffect, useState } from 'react';
import GmModal from 'app/shared/modal/modal';
import { useIssueReportService } from 'hooks/api/agents/issue-report';
import { useIssueService } from 'hooks/api/agents/issue';
import { StatusBadge } from './badges';
import { AddIssueToTenantDialog } from './add-issue-to-tenant';
import styles from '../issues.module.css';

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '';

const issueTitle = (report, titles) =>
  report.issue?.title || titles[report.issue_id] || `Issue #${report.issue_id}`;

export const TenantIssues = ({ tenant }) => {
  const { fetchIssueReports } = useIssueReportService();
  const { fetchIssue } = useIssueService();

  const [reports, setReports] = useState([]);
  const [titles, setTitles] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const tenant_id = tenant.id || tenant._id;

  const load = () => {
    setLoading(true);
    fetchIssueReports({
      query_string: `tenant.id=${tenant_id}&sort_by=-reported_at&population=20`
    })
      .then(async ({ reports: fetched }) => {
        const list = fetched || [];
        setReports(list);
        const missing = [
          ...new Set(list.filter((r) => !r.issue?.title && r.issue_id).map((r) => r.issue_id))
        ];
        if (missing.length) {
          const entries = await Promise.all(
            missing?.map(async (id) => {
              const { issue } = await fetchIssue(id);
              return [id, issue?.title || `Issue #${id}`];
            })
          );
          setTitles(Object.fromEntries(entries));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant_id]);

  return (
    <div>
      <div className={styles.tenantIssuesHeader}>
        <h2 className={styles.sectionTitle}>
          Issues
          {reports.length > 0 && <span className={styles.countPill}>{reports.length}</span>}
        </h2>
        <button type="button" className={styles.primaryBtn} onClick={() => setShowAdd(true)}>
          + Add issue
        </button>
      </div>

      {loading && <p className={styles.emptyText}>Loading…</p>}
      {!loading && reports.length === 0 && (
        <p className={styles.emptyText}>No issues reported by this tenant.</p>
      )}

      <div className={styles.tenantIssuesList}>
        {reports?.map((report) => (
          <div key={report.id || report._id} className={styles.tenantIssueRow}>
            <div className={styles.tenantIssueMain}>
              <span className={styles.tenantIssueTitle}>{issueTitle(report, titles)}</span>
              {report.details && <span className={styles.tenantIssueMeta}>{report.details}</span>}
              <span className={styles.tenantIssueMeta}>
                Reported {formatDate(report.reported_at)}
              </span>
            </div>
            <StatusBadge value={report.status} />
          </div>
        ))}
      </div>

      <GmModal
        title="Add issue to tenant"
        show_title
        show_modal={showAdd}
        onClose={() => setShowAdd(false)}
      >
        <AddIssueToTenantDialog tenant={tenant} onAdded={load} />
      </GmModal>
    </div>
  );
};

export default TenantIssues;
